import { useQueries } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import { useState, useEffect } from 'react';

import { useServicesDashboardDispatch } from 'context/ServicesDashboardContext';
import getService from 'API/Service/getService';
import { initServiceData } from 'pages/dashboards/v5/constants';

export const QUERY_CACHE_TIME = 1; // cache time set to 15 secs. (converted to milliseconds)
export const REFETCH_INTERVAL = 15 * 1000; // refetch interval set to 15 secs. (converted to milliseconds)

function useServiceDashboard({
    serviceType,
    serviceId,
    enabled,
    refetchInterval = REFETCH_INTERVAL,
}) {
    const { currentCompany: companyId } = useSelector(
        (state) => state.navigationLists,
    );
    const [isQueryDisabled, setIsQueryDisabled] = useState(false);
    const dispatch = useServicesDashboardDispatch();

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsQueryDisabled(true);
        }, 120000); // 2 minutes

        return () => clearTimeout(timer);
    }, []);

    return useQuery({
        queryKey: [
            `Service: ${serviceType}, ServiceID: ${serviceId}, CompanyID: ${companyId}`,
        ],
        queryFn: async () => {
            try {
                const res = await getService(serviceId);
                return res.data;
            } catch (error) {
                throw new Error(error);
            }
        },
        onSuccess: (data) => {
            if (initServiceData.serviceTypes.includes(serviceType)) {
                dispatch({
                    type: `${serviceType}/loaded_status`,
                    payload: {
                        serviceType,
                        service: data,
                    },
                });
            }
        },
        enabled:
            serviceType !== undefined &&
            serviceId !== undefined &&
            enabled &&
            !isQueryDisabled,
        retry: 3,
        cacheTime: QUERY_CACHE_TIME,
        refetchInterval,
        refetchOnWindowFocus: false,
    });
}

function useServiceDashboardBulk(items = [], { enabled = true } = {}) {
    const { currentCompany: companyId } = useSelector(
        (state) => state.navigationLists,
    );
    const [isQueryDisabled, setIsQueryDisabled] = useState(false);
    const dispatch = useServicesDashboardDispatch();

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsQueryDisabled(true);
        }, 120000); // 2 minutes

        return () => clearTimeout(timer);
    }, []);

    const queries = items.map(({ serviceType, serviceId }) => ({
        queryKey: [
            `Service: ${serviceType}, ServiceID: ${serviceId}, CompanyID: ${companyId}`,
        ],
        queryFn: async () => {
            const res = await getService(serviceId);
            return res.data;
        },
        onSuccess: (data) => {
            if (initServiceData.serviceTypes.includes(serviceType)) {
                dispatch({
                    type: `${serviceType}/loaded_status`,
                    payload: { serviceType, service: data },
                });
            }
        },
        enabled:
            enabled &&
            serviceType !== undefined &&
            serviceId !== undefined &&
            !isQueryDisabled,
        retry: 3,
        cacheTime: QUERY_CACHE_TIME,
        refetchInterval: REFETCH_INTERVAL,
        refetchOnWindowFocus: false,
    }));

    return useQueries({ queries });
}

export { useServiceDashboard, useServiceDashboardBulk };
